import React, {useEffect, useState} from 'react';
import {Text} from '@gravity-ui/uikit';
import {
  ActionModal,
  EntityImageUploadButton,
  ErrorModal,
  FormInput,
  useErrorHandler,
  usePublicImageUpload,
} from '../../../../shared';
import {useEditCategoryForm} from '../../model';
import styles from './EditCategoryModal.module.scss';

interface EditCategoryModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  categoryId: string | null;
}

export const EditCategoryModal = ({open, setOpen, categoryId}: EditCategoryModalProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    category,
  } = useEditCategoryForm({setOpen, categoryId});

  const {uploadId, isUploading, uploadImage, resetUpload} = usePublicImageUpload({
    resourceName: 'Изображение категории',
    notificationKey: 'category-image-upload-error',
  });
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Категория',
    title: 'Ошибка при редактировании категории',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
      setRemoveExistingImage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryId]);

  useEffect(() => {
    setFieldValue('imageUploadId', uploadId || null);
    if (uploadId) {
      setFieldValue('imageFileName', null);
    }
  }, [uploadId, setFieldValue]);

  useEffect(() => {
    if (removeExistingImage) {
      setFieldValue('imageFileName', null);
    }
  }, [removeExistingImage, setFieldValue]);

  const handleFileSelect = (file: File) => {
    setRemoveExistingImage(false);
    uploadImage(file);
  };

  const handleRemoveImage = () => {
    if (uploadId) {
      resetUpload();
      setFieldValue('imageUploadId', null);
    }
    if (category?.imageUrl && !removeExistingImage) {
      setRemoveExistingImage(true);
    }
  };

  const displayedImageUrl =
    !uploadId && !removeExistingImage ? category?.imageUrl ?? null : null;

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Редактировать категорию'}
        textButtonApply={'Сохранить'}
        textButtonCancel={'Отменить'}
        isDisabled={isUploading}>
        <FormInput
          label="Название"
          placeholder="Название категории"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && errors.name}
        />
        <FormInput
          label="Порядок отображения"
          placeholder="Порядок отображения"
          name="order"
          value={values.order.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.order && errors.order}
        />
        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Изображение
          </Text>
          <EntityImageUploadButton
            imageUrl={displayedImageUrl}
            isUploading={isUploading}
            onFileSelect={handleFileSelect}
            onRemove={handleRemoveImage}
            notificationKey="category-image-upload-error"
          />
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
